import { ApiResponse } from '@models/common/api';
import { IDeleteLink, ICreateBrandRequest } from '@models/social/request';
import { IFindAllBrands, IFindOneBrand, ISocialLinkResponse } from '@models/social/response';
import { ESocialPlatforms } from '@models/social/social';
import _ from 'lodash';

import { del, get, post, put } from './methods';

/**
 * Brand CRUD + social linking calls, parameterized by base path: Social uses `/brands`,
 * Messages `/messaging/brands` (listing-review routes without the Social plan guard). Same
 * request/response shapes on both, so one implementation keeps them from drifting apart.
 */
export function createBrandCrudApi(basePath: string) {
  return {
    // `messagingEnabled` is read-only (synced from Ayrshare): the listing-review proxy rejects
    // it in request bodies, but form values built from IFindAllBrands carry it along.
    create: async (body: ICreateBrandRequest, organizationId: number) => {
      const response = await post<ApiResponse<IFindOneBrand>>(basePath, {
        ..._.omit(body, ['messagingEnabled', 'phoneProvisioned']),
        organizationId,
      });
      return response.data;
    },
    edit: async (body: IFindAllBrands, id: number, organizationId: number) => {
      const response = await put<ApiResponse<IFindOneBrand>>(
        `${basePath}/${id}`,
        _.omit(body, ['id', 'messagingEnabled', 'phoneProvisioned']),
        { params: { organizationId } },
      );
      return response.data;
    },
    // Action-only update: asks the BE to call Ayrshare with messagingActive: true. The
    // confirmed state only comes back on the next findOne (Brand.messagingEnabled sync).
    activateMessaging: async (brandId: number, organizationId: number) => {
      const response = await put<ApiResponse<IFindOneBrand>>(
        `${basePath}/${brandId}`,
        { activateMessaging: true },
        { params: { organizationId } },
      );
      return response.data;
    },
    delete: async (brandId: number, organizationId: number) => {
      const response = await del<ApiResponse<IFindOneBrand>>(`${basePath}/${brandId}`, {
        params: { organizationId },
      });
      return response.data;
    },
    // `allowedSocial` limits the platforms shown on Ayrshare's link page; omitted → all.
    getLink: async (id: number, organizationId: number, allowedSocial?: ESocialPlatforms[]) => {
      const response = await post<ApiResponse<ISocialLinkResponse>>(
        `${basePath}/${id}/social-connection`,
        allowedSocial?.length ? { allowedSocial } : undefined,
        { params: { organizationId } },
      );
      return response.data;
    },
    deleteLink: async (id: number, organizationId: number, body: IDeleteLink) => {
      const response = await del<ApiResponse<ISocialLinkResponse>>(`${basePath}/${id}/social-connection`, {
        params: { organizationId },
        data: body,
      });
      return response.data;
    },
    findAll: async (organizationId: number) => {
      const response = await get<ApiResponse<IFindAllBrands[]>>(basePath, { params: { organizationId } });
      return response.data;
    },
    findOne: async (brandId: number, organizationId: number) => {
      const response = await get<ApiResponse<IFindOneBrand>>(`${basePath}/${brandId}`, { params: { organizationId } });
      return response.data;
    },
  };
}

export type BrandCrudApi = ReturnType<typeof createBrandCrudApi>;
